import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { concat } from '@ember/helper';
import { modifier } from 'ember-modifier';
import style from 'ember-style-modifier/modifiers/style';
import { setupCanvas, roundedRect } from '../../utils/charts/canvas-utils.ts';
import { linearScale, bandScale, niceAxisTicks } from '../../utils/charts/chart-scales.ts';
import { computeLayout, formatAxisValue, formatNumber } from '../../utils/charts/chart-layout.ts';
import {
  DEFAULT_CHART_COLORS,
  getThemeColors,
  hexToRgba,
} from '../../utils/charts/color-palette.ts';
import { createDebouncedResizeObserver } from '../../utils/charts/resize-observer-utils.ts';
import clampTooltip from '../../modifiers/clamp-tooltip.ts';
import type { ChartDataPoint, ChartOptions, AxisOptions } from '../../types/charts.ts';

interface HorizontalBarChartSignature {
  Args: ChartOptions &
    AxisOptions & {
      /** Chart data points */
      data: ChartDataPoint[];
      /** Min height (auto-grows with data length, min 300) */
      minHeight?: number;
      /** Whether to sort bars by value descending (default: true) */
      sorted?: boolean;
    };
  Element: HTMLDivElement;
}

const BAR_HEIGHT_PER_ITEM = 28;
const MIN_HEIGHT = 300;
const EMPTY_DATA: ChartDataPoint[] = [];

export default class HorizontalBarChart extends Component<HorizontalBarChartSignature> {
  @tracked containerWidth = 400;
  @tracked hoveredIndex: number | null = null;
  @tracked tooltipX = 0;
  @tracked tooltipY = 0;
  private _cachedDataRef: ChartDataPoint[] = EMPTY_DATA;
  private _cachedSorted = true;
  private _cachedSortedData: ChartDataPoint[] = EMPTY_DATA;

  get sortedData() {
    const data = this.args.data ?? EMPTY_DATA;
    const shouldSort = this.args.sorted !== false;

    if (this._cachedDataRef === data && this._cachedSorted === shouldSort) {
      return this._cachedSortedData;
    }

    const nextData = [...data];
    if (shouldSort) {
      nextData.sort((a, b) => b.value - a.value);
    }

    /* eslint-disable ember/no-side-effects -- deliberate memo cache on non-tracked private fields (avoids re-sorting + preserves array identity) */
    this._cachedDataRef = data;
    this._cachedSorted = shouldSort;
    this._cachedSortedData = nextData;
    /* eslint-enable ember/no-side-effects */

    return this._cachedSortedData;
  }

  get height() {
    const minH = this.args.minHeight ?? MIN_HEIGHT;
    return Math.max(minH, this.sortedData.length * BAR_HEIGHT_PER_ITEM + 60);
  }

  get colors() {
    return this.args.colors ?? DEFAULT_CHART_COLORS;
  }

  get tooltipData() {
    if (this.hoveredIndex === null || !this.sortedData[this.hoveredIndex]) return null;
    const d = this.sortedData[this.hoveredIndex]!;
    return { category: d.category, value: formatNumber(d.value) };
  }

  private barRects: Array<{ x: number; y: number; w: number; h: number; index: number }> = [];

  installCanvas = modifier((element: HTMLElement) => {
    const canvas = element.querySelector('canvas');
    if (!canvas) return;

    const observer = createDebouncedResizeObserver((entry) => {
      this.containerWidth = entry.contentRect.width;
      this.draw(canvas);
    });
    observer.observe(element);
    this.containerWidth = element.clientWidth || 400;
    this.draw(canvas);

    const onMove = (e: MouseEvent) => this.handleMouseMove(e, canvas);
    const onLeave = () => {
      if (this.hoveredIndex !== null) {
        this.hoveredIndex = null;
        this.draw(canvas);
      }
    };
    const onClick = (e: MouseEvent) => this.handleClick(e, canvas);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('click', onClick);

    return () => {
      observer.disconnect();
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('click', onClick);
    };
  });

  draw(canvas: HTMLCanvasElement) {
    const width = this.containerWidth;
    const height = this.height;
    const data = this.sortedData;
    if (!data.length) return;

    const ctx = setupCanvas(canvas, width, height);
    ctx.font = '500 12px system-ui, sans-serif';
    const longestLabel = Math.max(...data.map((d) => d.category.length));
    const maxMargin = Math.min(280, Math.floor(width * 0.4));
    const charWidth = ctx.measureText('M').width;
    const maxChars = Math.max(10, Math.floor((maxMargin - 16) / charWidth));
    const leftMargin = Math.min(
      maxMargin,
      Math.max(60, Math.min(longestLabel, maxChars) * charWidth + 16)
    );

    const layout = computeLayout(width, height, {
      left: leftMargin,
      right: 20,
      top: 20,
      bottom: 30,
    });
    const theme = getThemeColors();

    ctx.clearRect(0, 0, width, height);

    const maxVal = Math.max(...data.map((d) => d.value), 0);
    const ticks = niceAxisTicks(0, maxVal);
    const xScale = linearScale(
      [0, ticks[ticks.length - 1]!],
      [layout.left, layout.left + layout.innerWidth]
    );
    const categories = data.map((d) => d.category);
    const yScale = bandScale(categories, [layout.top, layout.top + layout.innerHeight], 0.15);

    // Vertical grid lines
    ctx.strokeStyle = theme.grid;
    ctx.lineWidth = 1;
    for (const tick of ticks) {
      const x = Math.round(xScale(tick)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(x, layout.top);
      ctx.lineTo(x, layout.top + layout.innerHeight);
      ctx.stroke();
    }

    // X axis tick labels (bottom)
    const formatFn = this.args.formatValue ?? formatAxisValue;
    ctx.fillStyle = theme.textMuted;
    ctx.font = '500 12px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (const tick of ticks) {
      ctx.fillText(formatFn(tick), xScale(tick), layout.top + layout.innerHeight + 6);
    }

    // Y axis labels (left)
    ctx.fillStyle = theme.textMuted;
    ctx.font = '500 12px system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    const bh = yScale.bandwidth();
    for (const cat of categories) {
      const label = cat.length > maxChars ? cat.slice(0, maxChars - 2) + '\u2026' : cat;
      ctx.fillText(label, layout.left - 8, yScale(cat) + bh / 2);
    }

    this.barRects = [];
    data.forEach((d, i) => {
      const y = yScale(d.category);
      const barW = xScale(d.value) - layout.left;
      const color = this.colors[i % this.colors.length]!;

      const lighter = adjustBrightness(color, 1.4);
      const isHovered = this.hoveredIndex === i;
      roundedRect(ctx, layout.left, y, Math.max(barW, 2), bh, 4);
      // Horizontal gradient: left (main) → right (lighter)
      if (typeof ctx.createLinearGradient === 'function') {
        const grad = ctx.createLinearGradient(layout.left, 0, layout.left + Math.max(barW, 2), 0);
        grad.addColorStop(0, isHovered ? adjustBrightness(color, 1.15) : color);
        grad.addColorStop(1, hexToRgba(lighter, isHovered ? 0.56 : 0.31));
        ctx.fillStyle = grad;
      } else {
        ctx.fillStyle = isHovered ? adjustBrightness(color, 1.15) : color;
      }
      ctx.fill();

      this.barRects.push({ x: layout.left, y, w: barW, h: bh, index: i });
    });
  }

  handleMouseMove(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const hit = this.barRects.find((r) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);

    if (hit) {
      if (this.hoveredIndex !== hit.index) {
        this.hoveredIndex = hit.index;
        this.tooltipX = rect.left + hit.x + hit.w + 8;
        this.tooltipY = rect.top + hit.y + hit.h / 2;
        this.draw(canvas);
      }
    } else if (this.hoveredIndex !== null) {
      this.hoveredIndex = null;
      this.draw(canvas);
    }
  }

  handleClick(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const hit = this.barRects.find((r) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);

    if (hit && this.args.onClick && this.sortedData[hit.index]) {
      this.args.onClick(this.sortedData[hit.index]!);
    }
  }

  <template>
    <div
      data-test-chart
      data-test-chart-type="horizontalBar"
      class="relative w-full h-full"
      {{this.installCanvas}}
      ...attributes
    >
      <canvas
        {{style width=(concat this.containerWidth "px") height=(concat this.height "px")}}
      ></canvas>

      {{#if this.tooltipData}}
        <div
          data-test-chart-tooltip
          class="tio-chart-tooltip -translate-y-1/2"
          {{style left=(concat this.tooltipX "px") top=(concat this.tooltipY "px")}}
          {{clampTooltip (concat this.tooltipX ":" this.tooltipY)}}
        >
          <div class="font-medium">{{this.tooltipData.category}}</div>
          <div class="tio-text-muted">{{this.tooltipData.value}}</div>
        </div>
      {{/if}}
    </div>
  </template>
}

function adjustBrightness(hex: string, factor: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v * factor)));
  const toHex = (v: number) => clamp(v).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export type { HorizontalBarChartSignature };
